import { SxProps } from '@mui/joy/styles/types';
import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { GetManyResponse } from 'core/types';
import { useGetDtdProject } from 'projects/hooks';
import { projectService } from 'projects/services';
import { IProject, IProjectAutocomplete, IProjectAutocompleteRequestPayload } from 'projects/types';

import { AlternateProjectOption } from './AlternateProjectOption';
import { UpsertProject } from '../UpsertProject';

export type TProjectRenderOptions = (
  option: IProjectAutocomplete,
  getLabel: (option: IProjectAutocomplete) => string,
  inputValue?: string
) => ReactNode;

export type TProjectGetOptions = (
  requestPayload: IProjectAutocompleteRequestPayload,
  signal?: AbortSignal
) => Promise<GetManyResponse<IProject>>;

export interface IProjectFormFieldProps {
  defaultDtdProject?: boolean;
  requestPayload?: IProjectAutocompleteRequestPayload;
  getOptions?: TProjectGetOptions;
  hasCreateOption?: boolean;
  onChange?: (project: IProjectAutocomplete) => void;
  hidden?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  disableClearable?: boolean;
  enabledOnFocus?: boolean;
  placeholder?: string;
  listboxStyle?: SxProps;
  label?: string;
  renderOptions?: TProjectRenderOptions;
}

export const ProjectFormField = ({
  defaultDtdProject = true,
  requestPayload: customRequestPayload = {},
  getOptions = projectService.getManyAutocomplete,
  hasCreateOption = true,
  onChange,
  hidden = false,
  disabled = false,
  autoFocus = false,
  required = true,
  disableClearable = true,
  enabledOnFocus = true,
  placeholder,
  listboxStyle,
  label,
  renderOptions: customRenderOptions,
}: IProjectFormFieldProps) => {
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [newProject, setNewProject] = useState(null);

  const { control, errors, setValue, watch } = useFormContext();
  const { user } = useAuthUser();

  const { record: dtdProject, isLoading: isDtdProjectLoading } = useGetDtdProject(
    defaultDtdProject && !hidden
  );

  const { t } = useTranslation();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const isCreateAllowed = useMemo(() => {
    return hasCreateOption && !isAlternateVersionEnabled;
  }, [hasCreateOption, isAlternateVersionEnabled]);

  const projectValue = watch('project');

  const requestPayload = useMemo(() => {
    return {
      ...customRequestPayload,
      filters: {
        access: { actionType: 'edit' },
        active: 1,
        alternate: isAlternateVersionEnabled ? 1 : 0,
        ...(customRequestPayload?.filters ?? {}),
      },
    };
  }, [customRequestPayload, isAlternateVersionEnabled]);

  useEffect(() => {
    if (hidden) {
      return;
    }

    if (defaultDtdProject && !isDtdProjectLoading && !projectValue?.name) {
      setValue('project', dtdProject);
    }
  }, [
    defaultDtdProject,
    disabled,
    dtdProject,
    hidden,
    isDtdProjectLoading,
    projectValue,
    setValue,
  ]);

  const onAddNewProject = (option: IProjectAutocomplete) => {
    if (!isCreateAllowed) {
      return;
    }

    setIsCreateProjectOpen(true);
    setNewProject(option);
  };

  const handleOnCloseAddNewProject = (project?: IProjectAutocomplete) => {
    setIsCreateProjectOpen(false);
    setValue('project', project);
  };

  const alternateRenderOptions = (
    option: IProjectAutocomplete,
    _: (option: IProjectAutocomplete) => string,
    inputValue?: string
  ) => <AlternateProjectOption option={option} inputValue={inputValue} />;

  return (
    <>
      <FormField
        name="project"
        type={InputType.AUTOCOMPLETE}
        hidden={hidden}
        inputProps={{
          control,
          error: errors?.project,
          label: label === undefined ? t('labelProject') : label,
          placeholder,
          optionKey: 'name',
          getOptions,
          onNewOption: onAddNewProject,
          onChange: (_: SyntheticEvent, option: IProjectAutocomplete) =>
            onChange && onChange(option),
          requestPayload,
          getLabel: (option: IProject) => option?.name,
          renderOptions:
            customRenderOptions ?? (isAlternateVersionEnabled ? alternateRenderOptions : undefined),
          required,
          disabled,
          autoFocus,
          hasCreateOption: isCreateAllowed,
          disableClearable,
          enabledOnFocus,
          listboxStyle,
        }}
      />

      {isCreateAllowed && (
        <UpsertProject
          isOpen={isCreateProjectOpen}
          onClose={handleOnCloseAddNewProject}
          initialValues={isCreateProjectOpen ? newProject : null}
          level={1}
        />
      )}
    </>
  );
};
